import PropType from "prop-types";

import classNames from "classnames";
import { generatePath, Link } from "react-router-dom";
import Problem from "../../assets/icons/Problem";
import { useTranslate } from "../../language/i18n";
import Label from "../shared/Label/Label";

import { Button, THEME } from "@myloc/myloc-gui";
import { useState } from "react";
import { useSelector } from "react-redux";
import useIdempotentSubmit from "../../hooks/useIdempotentSubmit";
import { setMessage } from "../../reducers/dialog/dialogAction";
import baseStorageService from "../../services/baseStorage/baseStorageService";
import orderService from "../../services/order/orderService";
import { CLIENT_TYPE, MOVE_OPTIONS } from "../../utils/constants";
import pages from "../../utils/pages";
import ProductCatalogueOrderButton from "../Products/ProductCatalogue/Components/ProductCatalogueOrderButton";
import { DELIVERY_LOCATION, ORDER_CONTROL } from "../Products/ProductDetail/Components/productConstants";
import ScrapModal from "../Scrap/ScrapModal";
import Counter from "../shared/Counter/Counter";
import styles from "./BaseStoragesPage.module.scss";
import OrderBaseStorageMoveModal from "./Move/OrderBaseStorageMoveModal";

const BaseStorageItem = ({ item, baseStorage, hideLinks, showBaseStorageMoveOption, onSuccessfulMove }) => {
  const translate = useTranslate();
  const order = useSelector(state => state.appData?.order);
  const [showScrapModal, setShowScrapModal] = useState(false);
  const [showOrderMoveModal, setShowOrderMoveModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [cartKey, setCartKey] = useState(0);

  const availableBalance = Math.max(0, (item?.product?.quantity ?? 0) - (item?.product?.allocatedQuantity ?? 0));
  const isUserOrder = order != null && order.receiver?.type === CLIENT_TYPE.USER;
  const showAddToCart = availableBalance > 0 && isUserOrder;

  const scrapSubmit = async (reasonCodeId, quantity) => {
    const data = { balances: [{ balanceId: item.id, quantity }], reasonCodeId };
    return await baseStorageService.scrapBalance(data);
  };

  const submitOrderMove = async ({ idempotencyKey }, data) => {
    const response = await orderService.createContent(data, { idempotencyKey });
    if (response.isOk()) {
      setShowOrderMoveModal(false);
      setMessage(translate("CART_ADDED_ITEM", { item: item.product.name }));
    }
    return response;
  };

  const [orderMove] = useIdempotentSubmit(submitOrderMove);

  const performMove = async (data, balanceId) => {
    const response = await baseStorageService.moveBalance(balanceId, data);
    if (response.isOk()) {
      setMessage(translate("ITEM_MOVED", { item: item.product.name }));
      onSuccessfulMove();
    }
  };
  const submitAddToCart = async ({ idempotencyKey }) => {
    const safeQuantity = Number(quantity) || 1;
    const data = {
      contentType: DELIVERY_LOCATION.BASE_STORAGE.id,
      subContentType: { id: ORDER_CONTROL.NONE.id },
      product: {
        id: item.product.inventoryNumber ? item.product.id : item.product.productId,
        quantity: safeQuantity,
      },
      fromFacility: {
        id: baseStorage?.id,
      },
    };
    const response = await orderService.createContent(data, { idempotencyKey });
    if (response.isOk()) {
      setMessage(translate("CART_ADDED_ITEM", { item: item.product.name }));
      setQuantity(1);
      setCartKey(prev => prev + 1);
    }
    return response;
  };

  const [onAddToCart, isAddingToCart] = useIdempotentSubmit(submitAddToCart);

  const getToBaseStorage = () => {
    if (showOrderMoveModal === MOVE_OPTIONS.DIRECT_MOVE) return null;
    if (order?.receiver?.type === CLIENT_TYPE.BASE_STORAGE) return order?.receiver;

    return baseStorage;
  };

  return (
    <article className={styles.item}>
      <div className={styles.itemLayout}>
        <div>
          <section className={classNames(styles.section, item.deviation && styles.titleDeviation)}>
            <div>
              <p className={styles.itemTitle}>
                {item?.product?.name || item?.product?.id}
                {item?.product?.hasDeviation && <Problem customCssClass={styles.deviation} />}
              </p>
            </div>
          </section>
          <section className={styles.section}>
            <div>
              {hideLinks ? (
                <Label label={translate("PRODUCT_NUMBER_SHORT")} value={item?.product?.productNumber} inline />
              ) : (
                <Link
                  to={{
                    pathname: generatePath(pages.PRODUCT_DETAIL.PATH, {
                      product: item?.product?.productId,
                    }),
                    state: { referrer: `${window.location.pathname}${window.location.search}` },
                  }}
                >
                  <Label label={translate("PRODUCT_NUMBER_SHORT")} value={item?.product?.productNumber} inline />
                </Link>
              )}
              <Label label={translate("ARTICLE_CLASS")} value={item?.product?.itemClass?.label} inline />
            </div>
            <div>
              {hideLinks || !item?.product?.inventoryNumber ? (
                <Label label={translate("INVENTORY_NUMBER_SHORT")} value={item?.product?.inventoryNumber} inline />
              ) : (
                <Link
                  to={{
                    pathname: generatePath(pages.FIXED_ASSET.PATH, {
                      baseStorage: baseStorage.id,
                      product: item?.product?.id,
                    }),
                    state: {
                      baseStorage: baseStorage,
                      referrer: `${window.location.pathname}${window.location.search}`,
                    },
                  }}
                >
                  <Label label={translate("INVENTORY_NUMBER_SHORT")} value={item?.product?.inventoryNumber} inline />
                </Link>
              )}
              <Label label={translate("BATCH_NO")} value={item?.product?.batch} inline />
            </div>
            <div>
              <Label label={translate("STORAGE_PLACE")} value={item?.location?.value} inline />
              <Label label={translate("SUPPLIER")} value={item?.product?.supplier?.label} inline />
            </div>
          </section>
        </div>
        <div className={styles.rightPanel}>
          <div className={styles.balanceInfo}>
            <p className={styles.availableBalanceTitle}>
              <span className={styles.availableBalanceLabel}>{translate("AVAILABLE_BALANCE")}:</span>{" "}
              <span className={styles.balanceValue}>
                {availableBalance} {item?.product?.unit}
              </span>
            </p>
            <p className={styles.balanceDetails}>
              <span className={styles.balanceLabel}>{translate("STOCK_BALANCE")}</span>{" "}
              <span className={styles.balanceValue}>{item?.product?.quantity}</span>
              <span className={styles.balanceDot}>&middot;</span>
              <span className={styles.balanceLabel}>{translate("RESERVED")}</span>{" "}
              <span className={styles.balanceValue}>{item?.product?.allocatedQuantity}</span>
            </p>
          </div>
          {showAddToCart && (
            <div className={styles.addToCart}>
              <Counter key={cartKey} startValue={1} min={1} max={availableBalance} onChange={setQuantity} />
              <ProductCatalogueOrderButton
                hasOrder={order != null}
                onClick={onAddToCart}
                product={{ id: item.product.inventoryNumber ? item.product.id : item.product.productId }}
                disabled={isAddingToCart}
              />
            </div>
          )}
          {availableBalance > 0 && (
            <div className={styles.buttons}>
              {(!order || order?.receiver?.type === CLIENT_TYPE.USER) && (
                <Button theme={THEME.PLAIN_PRIMARY} onClick={() => setShowOrderMoveModal(MOVE_OPTIONS.DIRECT_MOVE)}>
                  {translate("MOVE")}
                </Button>
              )}
              {showBaseStorageMoveOption && (
                <Button theme={THEME.PLAIN_PRIMARY} onClick={() => setShowOrderMoveModal(MOVE_OPTIONS.ORDER_MOVE)}>
                  {translate("ORDER_MOVE")}
                </Button>
              )}
              <Button theme={THEME.PLAIN_PRIMARY} onClick={() => setShowScrapModal(true)}>
                {translate("SCRAP")}
              </Button>
            </div>
          )}
        </div>
      </div>
      {showScrapModal && (
        <ScrapModal
          visible={showScrapModal}
          onClose={() => setShowScrapModal(false)}
          currentAvailableBalance={availableBalance}
          currentAvailableBalanceUnit={item?.product?.unit}
          isInventory={item?.product?.inventoryNumber != null}
          showCounter
          onSubmit={scrapSubmit}
        />
      )}

      <OrderBaseStorageMoveModal
        visible={showOrderMoveModal}
        onClose={() => setShowOrderMoveModal(false)}
        item={item}
        onSubmit={showOrderMoveModal === MOVE_OPTIONS.DIRECT_MOVE ? performMove : orderMove}
        fromBaseStorage={baseStorage}
        toBaseStorage={getToBaseStorage()}
        isDirectMove={showOrderMoveModal === MOVE_OPTIONS.DIRECT_MOVE}
      />
    </article>
  );
};

BaseStorageItem.propTypes = {
  item: PropType.object,
  baseStorage: PropType.object,
  hideLinks: PropType.bool,
  showBaseStorageMoveOption: PropType.bool,
  onSuccessfulMove: PropType.func,
};

export default BaseStorageItem;
